import { useEffect, useState } from "react";
import { X, Hammer } from "lucide-react";

/**
 * Discrete bottom-left pill that shows "Preview mode" when the app is running
 * on an Emergent preview domain (`*.preview.emergentagent.com`). Auto-hides on
 * any other host so production deployments never show it.
 *
 * Behavior:
 *  - Only renders when hostname matches the preview pattern
 *  - Dismissible per session via the X button
 *  - Stays out of the way: bottom-left, tiny, dark, semi-transparent
 *  - Lives outside main app shell so it shows on every route
 */
export default function PreviewBadge() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const isPreview = /\.preview\.emergentagent\.com$/i.test(
            window.location.hostname
        );
        const dismissed = sessionStorage.getItem("bl-preview-pill-dismissed") === "1";
        setVisible(isPreview && !dismissed);
    }, []);

    if (!visible) return null;

    const dismiss = () => {
        try {
            sessionStorage.setItem("bl-preview-pill-dismissed", "1");
        } catch {
            /* no-op */
        }
        setVisible(false);
    };

    return (
        <div
            className="fixed bottom-4 left-4 z-[60] flex items-center gap-2 rounded-full bg-[#1F2428]/90 text-white pl-3 pr-1.5 py-1.5 shadow-lg backdrop-blur-sm border border-white/10 text-xs font-medium animate-in fade-in slide-in-from-bottom-2"
            data-testid="preview-mode-pill"
            role="status"
            aria-label="You are viewing the preview environment"
        >
            <Hammer className="w-3.5 h-3.5 text-amber-300" aria-hidden />
            <span>Preview mode · not production</span>
            <button
                type="button"
                onClick={dismiss}
                className="ml-1 p-1 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Dismiss preview banner"
                data-testid="preview-mode-pill-dismiss"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
}
